import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { FiMail, FiMessageCircle, FiClock, FiShield, FiHelpCircle, FiChevronDown, FiChevronUp, FiCheckCircle } from "react-icons/fi";

const SUPPORT_EMAIL = "truth.ai.tech@gmail.com";

const APPLICATIONS = [
  { value: "", label: "Select application" },
  { value: "Truth-AI-Tech (Website)", label: "Truth-AI-Tech (Website)" },
  { value: "DivExp App", label: "DivExp App" },
  { value: "FitPost App", label: "FitPost App" },
];

const faqs = [
  {
    question: "How do I report a bug or issue?",
    answer: "You can report bugs by sending an email to our support team with a detailed description of the issue, including steps to reproduce it, your device model, and OS version. Screenshots or screen recordings are also helpful."
  },
  {
    question: "How can I request a new feature?",
    answer: "We love hearing from our users! Send us an email with your feature request, and our team will review it for future updates. Please include as much detail as possible about how the feature would benefit you."
  },
  {
    question: "Is my data secure?",
    answer: "Yes, we take data security seriously. We follow industry best practices to protect your information and never sell your personal data to third parties. Please refer to our Privacy Policy for more details."
  },
  {
    question: "How do I delete my account or data?",
    answer: "To request account deletion or data removal, please send an email to our support team. We will process your request in accordance with applicable privacy laws and respond within 30 days."
  },
  {
    question: "What are your support hours?",
    answer: "Our support team typically responds within 24-48 hours during business days. For urgent issues, please mark your email as high priority."
  },
  {
    question: "Do you offer refunds?",
    answer: "Refund policies depend on the platform where you made your purchase (App Store, Google Play, etc.). Please contact the respective platform for refund requests, or reach out to us if you need assistance."
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 px-1 flex justify-between items-center text-left hover:bg-gray-50 transition-colors rounded-lg"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-gray-800 pr-4">{question}</span>
        {isOpen ? (
          <FiChevronUp className="text-blue-500 flex-shrink-0" size={20} />
        ) : (
          <FiChevronDown className="text-gray-400 flex-shrink-0" size={20} />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 px-1 text-gray-600 leading-relaxed animate-fade-in">
          {answer}
        </div>
      )}
    </div>
  );
}

function Support() {
  const [formData, setFormData] = useState({
    application: "",
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create mailto link with form data
    const bodyLines = [
      formData.application && `Application: ${formData.application}`,
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      "",
      "Message:",
      formData.message
    ].filter(Boolean).join("\n");
    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(formData.subject || "Support Request")}&body=${encodeURIComponent(bodyLines)}`;
    window.location.href = mailtoLink;
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Helmet>
        <title>Support & Contact Us | Truth AI Tech</title>
        <meta name="description" content="Get help and support. Contact our team for assistance with any questions, issues, or feedback." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">How Can We Help?</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            We're here to assist you. Reach out to our support team and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Quick Info Cards */}
      <section className="max-w-5xl mx-auto px-4 -mt-8">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiMail className="text-blue-600" size={24} />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Email Support</h3>
            <a 
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-blue-600 hover:text-blue-700 break-all"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiClock className="text-green-600" size={24} />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Response Time</h3>
            <p className="text-gray-600">Within 24-48 hours</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiShield className="text-purple-600" size={24} />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Privacy First</h3>
            <p className="text-gray-600">Your data is secure with us</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <FiMessageCircle className="text-blue-600" size={28} />
              <h2 className="text-2xl font-bold text-gray-800">Send Us a Message</h2>
            </div>
            
            {isSubmitted ? (
              <div className="text-center py-12 animate-fade-in">
                <FiCheckCircle className="text-green-500 mx-auto mb-4" size={48} />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Email Client Opened!</h3>
                <p className="text-gray-600">Please send the email from your mail app to complete your request.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="application" className="block text-sm font-medium text-gray-700 mb-2">
                    Application
                  </label>
                  <select
                    id="application"
                    name="application"
                    value={formData.application}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-white"
                  >
                    {APPLICATIONS.map((app) => (
                      <option key={app.value || "default"} value={app.value}>
                        {app.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Which product or app is this about?</p>
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-white"
                  >
                    <option value="">Select a topic</option>
                    <option value="Bug Report">Bug Report</option>
                    <option value="Feature Request">Feature Request</option>
                    <option value="Account Issue">Account Issue</option>
                    <option value="Billing Question">Billing Question</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Privacy/Data Request">Privacy/Data Request</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none resize-none"
                    placeholder="Please describe your issue or question in detail..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
                >
                  Send Message
                </button>
              </form>
            )}
          </div>

          {/* FAQ Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <FiHelpCircle className="text-blue-600" size={28} />
              <h2 className="text-2xl font-bold text-gray-800">Frequently Asked Questions</h2>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Direct Email CTA */}
      <section className="bg-gray-800 text-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Prefer to Email Directly?</h2>
          <p className="text-gray-300 mb-6">
            You can reach us anytime at our support email address.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 bg-white text-gray-800 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            <FiMail size={20} />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="mb-2">&copy; {new Date().getFullYear()} Truth AI Tech. All rights reserved.</p>
          <p className="text-sm">
            We're committed to providing excellent support and protecting your privacy.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Support;
